import React, {
  FormEvent,
  useCallback,
  useMemo,
  useReducer,
  useState,
} from 'react';

import {
  CoreResponseType,
  DisplayConfiguration,
  SubmitResponse,
  TFormData,
} from '#/client/models';
import { FormInputVariants } from '#/service/constants';
import { modalActions } from '#/store/modal/modal.store';
import { tb } from '#/store/translation/translation.store';

import {
  FormAction,
  FormProps,
  InputButton,
  InputButtonSubmit,
  InputFieldGroup,
  InputGroupType,
  TInputState,
  TValidation,
  ValidationListType,
} from './Form.types';
import {
  clearForm,
  getInitialState,
  getMainErrorMessage,
  reducer,
  validateMatch,
  validateRegexp,
  validateTimestamp,
} from './Form.utils';

interface Props {
  inputGroups: FormProps['inputGroups'];
  /** @see CommonFormProps.registrationHackMode */
  registrationHackMode?: FormProps['registrationHackMode'];
}

export const useForm = ({ inputGroups, registrationHackMode }: Props) => {
  const [formState, dispatch] = useReducer(
    reducer,
    getInitialState(inputGroups)
  );

  const [activeButtonKey, setActiveButtonKey] = useState<string>();
  const [isFormValid, setIsFormValid] = useState<boolean>();

  const validationList = useMemo(
    () =>
      inputGroups.reduce((list: ValidationListType[], inputGroup) => {
        if (inputGroup.type === InputGroupType.Field) {
          inputGroup.inputs.forEach((input) => {
            list.push({
              key: input.key,
              groupKey: inputGroup.key,
              validations: input.validations ?? [],
            });
          });
        }

        return list;
      }, []),
    [inputGroups]
  );

  const checkValidity = (ref: HTMLFormElement) => {
    const isValid = ref?.checkValidity();

    if (isValid === isFormValid) {
      return;
    }

    setIsFormValid(isValid);
  };

  const updateState = useCallback((key: string, inputState: TInputState) => {
    const action = {
      type: FormAction.Update,
      payload: { key, inputState },
    };

    dispatch(action);
  }, []);

  // allow a field to get the value of another field for cross-field validation
  const getFieldValue = useCallback(
    (key: string = '') => {
      if (!(formState.inputs && Object.hasOwn(formState.inputs, key))) {
        console.debug(`Form - no input field for [${key}].`);

        return;
      }

      return formState.inputs[key].value;
    },
    [formState.inputs]
  );

  const updateErrorMessages = useCallback(
    (key: string, ...errorMessages: (string | undefined)[]) => {
      const action = {
        type: FormAction.Error,
        payload: { key, errorMessages: errorMessages.filter(Boolean) },
      };

      dispatch(action);
    },
    []
  );

  const getFormData = useCallback(() => {
    return Object.keys(formState.inputs).reduce(
      (formData: TFormData, key: string) => {
        formData[key] = formState.inputs[key].value;

        return formData;
      },
      {}
    );
  }, [formState.inputs]);

  const handleSubmitError = useCallback(
    (res: SubmitResponse) => {
      const { message, error: { validation = {} } = {} } = res?._meta ?? {};
      const validationEntries = Object.entries(validation);
      // display validation errors under each invalid field
      validationEntries.forEach(([key, errorMessages]) => {
        if (formState.inputs[key]) {
          updateErrorMessages(key, ...errorMessages);
        }
      });
      // display the modal with an error message
      modalActions.showModal({
        buttonLabel: tb('button.ok'),
        message: getMainErrorMessage(validationEntries, message),
      });
    },
    [formState.inputs, updateErrorMessages]
  );

  const processValidations = useCallback(
    (
      validations?: TValidation[],
      inputValue?: string,
      displayConfiguration?: DisplayConfiguration
    ) => {
      // we don't validate empty. we have the "required" prop for that
      if (!inputValue) {
        return;
      }

      let msg: string | undefined;
      validations?.forEach((validation) => {
        // the single space fallback shows the red border, with no message. we might not get a proper message from Core with some providers.
        validation.message ??= ' ';
        const { message, regexp, match, timestamp } = validation;

        msg =
          validateRegexp(regexp, inputValue, message) ??
          validateMatch(match, inputValue, getFieldValue, message) ??
          validateTimestamp(
            timestamp,
            inputValue,
            message,
            displayConfiguration?.granularity
          );

        return msg;
      });

      return msg;
    },
    [getFieldValue]
  );

  const getMatchedField = (currentGroup: InputFieldGroup, dataKey: string) => {
    let localMsg;
    const matchedField = currentGroup.inputs.find((input) =>
      input?.validations?.some((validation) => {
        if (validation.match === dataKey) {
          localMsg = validation.message;

          return true;
        }

        return false;
      })
    );

    return { matchedField, localMsg };
  };

  const handleValidate = useCallback(
    (
      dataKey: string,
      groupKey: string,
      inputRef?: React.MutableRefObject<
        HTMLInputElement | HTMLTextAreaElement | null
      >,
      value?: string,
      displayConfiguration?: DisplayConfiguration
    ) => {
      inputRef?.current?.setCustomValidity('');
      let msg = inputRef?.current?.validationMessage ?? undefined;
      const inputValue = value ?? formState.inputs[dataKey].value?.toString();
      const validations = validationList.find(
        (validationItem) =>
          validationItem.key === dataKey && validationItem.groupKey === groupKey
      )?.validations;
      const validationMessage = processValidations(
        validations,
        inputValue,
        displayConfiguration
      );

      if (validationMessage !== undefined) {
        msg = validationMessage;
      }

      const currentGroup = inputGroups.find((group) => group.key === groupKey);

      // check if other input from field match has same value and if there's any error message shown
      if (currentGroup?.type === InputGroupType.Field && !msg) {
        const { matchedField, localMsg } = getMatchedField(
          currentGroup,
          dataKey
        );

        if (matchedField?.key) {
          const otherInputValue = getFieldValue(matchedField.key);

          updateErrorMessages(
            matchedField.key,
            otherInputValue && inputValue !== otherInputValue
              ? localMsg
              : undefined
          );
        }
      }

      updateErrorMessages(dataKey, msg);
    },
    [
      formState.inputs,
      getFieldValue,
      inputGroups,
      processValidations,
      updateErrorMessages,
      validationList,
    ]
  );

  const handleSubmit = useCallback(
    (
      buttonKey: string,
      submitForm: (
        data: TFormData,
        buttonText: string
      ) => Promise<SubmitResponse>,
      buttonText: string
    ) => {
      setActiveButtonKey(buttonKey);
      submitForm(getFormData(), buttonText)
        .then((res) => {
          const { type, ignoreError } = res?._meta ?? {};

          // TODO#registrationHackMode:
          //  temporary hack while we need `setTimeout` for `showPaywall` after registration.
          //  should be: `.finally(() => setActiveButtonKey(undefined))`
          //  and just: `clearForm(inputGroups, dispatch)` further down.
          if (!registrationHackMode) {
            setActiveButtonKey(undefined);
          }

          if (type === CoreResponseType.SUCCESS) {
            if (!registrationHackMode) {
              clearForm(inputGroups, dispatch);
            }
          } else if (type === CoreResponseType.ERROR) {
            setActiveButtonKey(undefined);

            if (!ignoreError) {
              handleSubmitError(res);
            }
          } else {
            // for errors that are not formatted as form errors, like a generic 404
            setActiveButtonKey(undefined);
            handleSubmitError(res);
          }
        })
        .catch((err) => {
          // catch-all fallback, just in case anything threw in the `then`
          console.error(err);
          setActiveButtonKey(undefined);
          handleSubmitError(err);
        });
    },
    [getFormData, handleSubmitError, inputGroups, registrationHackMode]
  );

  const isButtonDisabled = useCallback(
    (button: InputButton) => {
      if (
        activeButtonKey ||
        Object.values(formState.errorMessages).some(
          (errorMessage) => errorMessage?.length
        )
      ) {
        return true;
      }

      if (button.variant !== FormInputVariants.SUBMIT && !button.isMainButton) {
        return false;
      }

      return !isFormValid;
    },
    [activeButtonKey, formState.errorMessages, isFormValid]
  );

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const buttons = inputGroups.find((g) => g.type === InputGroupType.Button)
        ?.inputs as InputButton[];
      const submitButton = buttons.find(
        (i) => i.variant === FormInputVariants.SUBMIT
      ) as InputButtonSubmit;

      if (submitButton) {
        handleSubmit(submitButton.key, submitButton.onClick, submitButton.text);
      }
    },
    [handleSubmit, inputGroups]
  );

  return {
    checkValidity,
    getFormData,
    handleValidate,
    handleSubmit,
    isButtonDisabled,
    updateErrorMessages,
    updateState,
    handleFormSubmit,
    activeButtonKey,
    formState,
  };
};
